import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronRight, Loader2 } from "lucide-react";
import { SettingsMetaPage } from "./SettingsMetaPageLayout";
import { useBreadcrumb } from "@/contexts/BreadcrumbContext";

export function SettingsMetaBusinessAdAccountRoute() {
  const { businessId, adAccountId } = useParams<{ businessId: string; adAccountId: string }>();
  const { setBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchAdAccount() {
      if (!businessId || !adAccountId) return;

      setIsLoading(true);
      try {
        const [businessResponse, adAccountResponse] = await Promise.all([
          fetch(`/api/meta/businesses/${businessId}`),
          fetch(`/api/meta/ad-accounts/${adAccountId}`),
        ]);

        const businessName =
          businessResponse.ok
            ? ((await businessResponse.json()) as { business?: { name?: string } }).business
                ?.name || businessId
            : businessId;

        const adAccountName =
          adAccountResponse.ok
            ? ((await adAccountResponse.json()) as { adAccount?: { name?: string } }).adAccount
                ?.name || adAccountId
            : adAccountId;

        setBreadcrumbs([
          { label: "Settings", path: "/settings" },
          { label: "Integrations", path: "/settings/integrations" },
          { label: "Meta", path: "/settings/integrations/meta" },
          { label: businessName, path: `/settings/meta/businesses/${businessId}` },
          {
            label: "Ad Accounts",
            path: `/settings/meta/businesses/${businessId}/ad-accounts`,
          },
          {
            label: adAccountName,
            path: `/settings/meta/businesses/${businessId}/ad-accounts/${adAccountId}`,
          },
        ]);
      } catch (error) {
        console.error("Failed to fetch Meta ad account:", error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchAdAccount();

    return () => {
      clearBreadcrumbs();
    };
  }, [businessId, adAccountId, setBreadcrumbs, clearBreadcrumbs]);

  if (isLoading) {
    return (
      <SettingsMetaPage className="flex justify-center items-center min-h-[40vh]">
        <Loader2 className="animate-spin h-12 w-12 text-muted-foreground" />
      </SettingsMetaPage>
    );
  }

  return (
    <SettingsMetaPage>
      <Link
        to={`/settings/meta/businesses/${businessId}/ad-accounts/${adAccountId}/audiences`}
        className="flex items-center justify-between border rounded-lg p-4 hover:bg-muted/50 transition-colors"
      >
        <span className="font-medium">Audiences</span>
        <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
      </Link>
    </SettingsMetaPage>
  );
}
